import type { CustomFieldValue } from '@fukuri-admins/apis/client/gen/model';
import type { UserDetailProfilePresentation } from '../../presentations';

type DisplayItem = React.ComponentProps<
  typeof UserDetailProfilePresentation
>['customFields'][number];

/**
 * value を表示用文字列に整形する
 *
 * - value_type が 'bool' の場合は「はい」「いいえ」に変換する
 * - それ以外は文字列として返す
 */
const formatValue = (value: string | number | boolean, valueType?: string): string => {
  if (valueType === 'bool' || typeof value === 'boolean') {
    if (value === true || value === 'true') return 'はい';
    if (value === false || value === 'false') return 'いいえ';
  }
  return String(value);
};

/**
 * プロフィール API の custom_field_values を表示用アイテムリストに変換する
 *
 * - custom_field_values が空の場合は空配列を返す（Presentation でセクション非表示）
 * - value_type に応じた整形を行う（bool → 「はい」「いいえ」、それ以外はそのまま）
 * - 同じ label のエントリ（multi_choice で複数エントリが並ぶケース）は 1 行にまとめ、
 *   整形済み値を「, 」区切りで結合する。表示順は元配列の初出順を維持する
 */
export const convertCustomFieldsToDisplayItems = (
  customFieldValues: CustomFieldValue[]
): DisplayItem[] => {
  if (customFieldValues.length === 0) return [];

  const labelOrder: string[] = [];
  const grouped: Record<string, string[]> = {};

  for (const field of customFieldValues) {
    const formattedValue = formatValue(field.value, field.value_type);
    if (!(field.label in grouped)) {
      labelOrder.push(field.label);
      grouped[field.label] = [];
    }
    grouped[field.label].push(formattedValue);
  }

  return labelOrder.map(label => ({
    label,
    value: grouped[label].join(', '),
  }));
};
